import * as React from 'react';

import type { IListAssignee, IListFieldCatalogue, ListFieldSource } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import {
  fieldsByCode,
  leavesOf,
  type ICriteriaGroup,
  type ICriteriaWords,
  type KindOf
} from '../../list-criteria';
import { ASSIGNED_TO } from './editing';

const NO_ACCOUNTS: readonly IListAssignee[] = [];

/**
 * What a list's screen looks its catalogue up by (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3): a field by its code, its
 * source and kind, and the accounts survey-api offers to assign — read once per screen — with the
 * words a filter is said in. Lists and a report's builder read the same, so they read it here.
 *
 * **A failed read of the accounts is not an empty list**: `assignees.error` is said by the screen,
 * and an *Assigned to* control is not offered over it — a picker of nobody would read as a list
 * nobody can be assigned from.
 */
export function useCatalogueLookups(catalogue: IListFieldCatalogue) {
  const fields = React.useMemo(() => fieldsByCode(catalogue), [catalogue]);
  const sources = React.useMemo(() => {
    const map = new Map<string, ListFieldSource>();
    for (const group of catalogue.groups) {
      for (const field of group.fields) map.set(field.code, group.source);
    }
    return map;
  }, [catalogue]);
  const fieldOf = React.useCallback((code: string) => fields.get(code), [fields]);
  const sourceOf = React.useCallback((code: string) => sources.get(code), [sources]);
  const kindOf: KindOf = React.useCallback((code: string) => fields.get(code)?.kind, [fields]);
  const assignees = useAdminData((signal) => listsApi.assignees(signal), []);
  const accounts = assignees.data ?? NO_ACCOUNTS;
  const emails = React.useMemo(
    () => new Map(accounts.map((account) => [account.accountId, account.email])),
    [accounts]
  );
  const emailOf = React.useCallback((accountId: string) => emails.get(accountId), [emails]);
  const words: ICriteriaWords = React.useMemo(() => ({ fieldOf, personOf: emailOf }), [fieldOf, emailOf]);
  return { fields, fieldOf, sourceOf, kindOf, assignees, accounts, emailOf, words };
}

/**
 * A person field's accounts as survey-api's facet read names them — `value` the account id, `label`
 * its email (SR-408's D8): the people who hold the field on some row of the survey, which for
 * *Last edited by* or *Created by* include advisors the assignable accounts never list. The
 * commonest 500 (survey-api's cap); an account past them is not named — unless `q` narrows the read
 * first, which survey-api matches against the value as well as the label, so an account's id names
 * that account alone.
 */
export async function personFacets(
  survey: string,
  field: string,
  signal?: AbortSignal,
  q?: string
): Promise<Map<string, string>> {
  const facets = await listsApi.facets({ query: { survey }, field, ...(q ? { q } : {}) }, signal);
  const named = new Map<string, string>();
  for (const facet of facets.values) {
    if (typeof facet.value === 'string' && facet.label) named.set(facet.value, facet.label);
  }
  return named;
}

/** The account ids a person leaf's value holds — one, or a list's — the blank left out. */
export function accountIdsOf(value: unknown): string[] {
  return (Array.isArray(value) ? value : [value]).filter(
    (id): id is string => typeof id === 'string' && id !== ''
  );
}

/** Whether a field's values are its facets' accounts — a person field other than *Assigned to*,
 *  whose values are the assignable accounts. */
export function isFacetPerson(field: { code: string; kind: string } | undefined): boolean {
  return field?.kind === 'person' && field.code !== ASSIGNED_TO;
}

/**
 * The words a list's criteria are said in, an account named (SR-408's D8) by the assignees' email,
 * else by the label a facet read gave it, else as spelled: **one read per account the criteria
 * name on such a field**, narrowed by its id (`q`), made as the screen draws them and kept for the
 * screen — a chip on *Last edited by* says the advisor's email after a reload, which the assignees
 * never list, however many people the survey's rows name. A read that fails names nobody; the id
 * stays as spelled, and is asked again when the criteria next change.
 */
export function useCriteriaWords(
  survey: string,
  root: ICriteriaGroup,
  words: ICriteriaWords
): ICriteriaWords {
  const [named, setNamed] = React.useState<ReadonlyMap<string, string>>(new Map());
  const asked = React.useRef(new Set<string>());
  const wanted = leavesOf(root)
    .filter((leaf) => isFacetPerson(words.fieldOf(leaf.field)))
    .flatMap((leaf) =>
      accountIdsOf(leaf.value).map((id) => ({ field: leaf.field, id, asking: `${leaf.field} ${id}` }))
    );
  const key = wanted.map(({ asking }) => asking).join('\n');
  React.useEffect(() => {
    for (const { field, id, asking } of wanted) {
      if (asked.current.has(asking) || words.personOf?.(id)) continue;
      asked.current.add(asking);
      personFacets(survey, field, undefined, id)
        .then((more) => setNamed((held) => new Map([...held, ...more])))
        .catch(() => asked.current.delete(asking));
    }
    // `key` stands for `wanted`, rebuilt on every render.
  }, [survey, key]);
  return React.useMemo(
    () => ({
      ...words,
      personOf: (accountId: string) => words.personOf?.(accountId) ?? named.get(accountId)
    }),
    [words, named]
  );
}
